import * as React from 'react';

import { useApolloClient } from '@apollo/client';

import type { UserFilter } from 'bundles/enterprise-admin-user-filters/types';
import { mapUserFilter } from 'bundles/enterprise-admin-user-filters/utils';
import EnterpriseUserAdminViewCountByFilter from 'bundles/enterprise-admin-users/queries/EnterpriseUserAdminViewCountByFilter.graphql';
import type {
  EnterpriseUserAdminViewCountByFilterQuery,
  EnterpriseUserAdminViewCountByFilterQueryVariables,
} from 'bundles/enterprise-admin-users/queries/__generated__/EnterpriseUserAdminViewCountByFilter';

/**
 * Resolves the audience size via the shared `EnterpriseUserAdminView.countByFilter`
 * query — the exact same call (and `mapUserFilter` payload) the existing messaging
 * page + filter `MatchCounter` use. Built from the SAME client `UserFilter` as the
 * list and the saved filter, so the displayed count matches who the message reaches.
 * Returns `null` on any failure so the modal can fall back gracefully.
 */
export const useAudienceCount = () => {
  const client = useApolloClient();

  return React.useCallback(
    async (orgId: string, userFilter: UserFilter, userIdentifiers?: string[]): Promise<number | null> => {
      try {
        const filter = mapUserFilter(userFilter, undefined, undefined, undefined, userIdentifiers);
        const result = await client.query<
          EnterpriseUserAdminViewCountByFilterQuery,
          EnterpriseUserAdminViewCountByFilterQueryVariables
        >({
          query: EnterpriseUserAdminViewCountByFilter,
          // SCIM-managed split is a count-display concern the messaging flow never
          // turns on (matches `MatchCounter`): pass the same filter twice, flag off.
          variables: {
            filterForTotalCount: filter,
            filterForScimManagedUsers: filter,
            orgId,
            shouldCheckForScimManagedUsers: false,
          },
          context: { clientName: 'gatewayGql' },
          fetchPolicy: 'no-cache',
        });
        return result.data?.EnterpriseUserAdminView?.totalCount ?? null;
      } catch {
        return null;
      }
    },
    [client]
  );
};

export default useAudienceCount;
